import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Crown, Eye, EyeOff, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function SetupPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Wachtwoorden komen niet overeen'); return; }
    if (password.length < 8) { toast.error('Wachtwoord moet minimaal 8 tekens zijn'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/setup-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Er ging iets mis. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center p-4">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4A017] via-[#f5c842] to-[#D4A017]" />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#D4A017] rounded-xl flex items-center justify-center">
              <Crown className="w-7 h-7 text-[#0f172a]" />
            </div>
            <span className="text-2xl font-bold text-white font-serif">IMPERIA AI</span>
          </Link>
          {done ? (
            <>
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Wachtwoord ingesteld!</h1>
              <p className="text-[#64748b]">Je wordt doorgestuurd naar de inlogpagina...</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-white mb-2">Wachtwoord instellen</h1>
              <p className="text-[#64748b]">Welkom bij IMPERIA AI. Stel je wachtwoord in om te beginnen.</p>
            </>
          )}
        </div>

        {!done && (
          <div className="bg-[#0f172a] rounded-2xl border border-white/10 p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#94a3b8] mb-2">Wachtwoord</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Minimaal 8 tekens"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#475569] focus:outline-none focus:border-[#D4A017] pr-12"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#D4A017]">
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#94a3b8] mb-2">Bevestig wachtwoord</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Herhaal wachtwoord"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#475569] focus:outline-none focus:border-[#D4A017]"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !token}
                className="w-full bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold py-3.5 rounded-xl transition-all disabled:opacity-60"
              >
                {loading ? 'Bezig...' : 'Wachtwoord instellen & inloggen'}
              </button>
              {!token && <p className="text-red-400 text-sm text-center">Ongeldige link. Neem contact op met IMPERIA AI.</p>}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
